import { displayText, clientUserText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import {
  createArbitration,
  updateArbitration,
  getArbitration,
  getCsrfToken,
  getCase,
} from '../../../../services/api';
import { validateField, validateForm } from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import ClientConfig from '../../../../../config/Clientconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';


const SC = ClientConfig.sessions.types.arbitration;

const AddArbitration = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username, caseId: paramCaseId, arbitrationId } = useParams();
  const navigate = useNavigate();

  const isEditMode = Boolean(arbitrationId);

  const generatePasscode = () =>
    Math.floor(10000 + Math.random() * 90000).toString();

  const [caseId, setCaseId] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [touched, setTouched] = useState({});


  const [formData, setFormData] = useState({
    name: '',
    introducing_party: [],
    date: '',
    details: '',
    location: '',
    session_passcode: generatePasscode(),
    schedule_video: false,
    zoom_tech: '',
    registrants: '',
    meeting_duration: '60',
    meeting_time: '',
    meeting_timezone: '',

    // Zoom meeting fields
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_password: '',
    zoom_created_at: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ================= CSRF & USERS ================= */
  useEffect(() => {
    getCsrfToken();

    const fetchCombinedUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/client-users/combined-for-introducing`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          setCombinedUsers(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching combined users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchCombinedUsers();
  }, []);

  /* ================= LOAD CASE ID AND ARBITRATION (IF EDIT) ================= */
  useEffect(() => {
    if (!paramCaseId || isNaN(paramCaseId)) {
      toast.error(displayText('Invalid case ID'));
      navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      return;
    }

    setCaseId(parseInt(paramCaseId, 10));

    if (isEditMode) {
      if (!arbitrationId || isNaN(arbitrationId)) {
        toast.error(displayText('Invalid arbitration ID'));
        navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
        return;
      }

      const loadArbitration = async () => {
        try {
          console.log('paramCaseId:', paramCaseId);
          console.log('arbitrationId:', arbitrationId);

          const res = await getArbitration(arbitrationId);
          console.log('arbitration response:', res.data);

          const arbitration = res.data?.data || res.data;

          if (!arbitration || !arbitration.id) {
            throw new Error('Invalid arbitration response');
          }

          if (Number(arbitration.case_id) !== Number(paramCaseId)) {
            throw new Error('Arbitration does not belong to this case');
          }

          setFormData({
            name: arbitration.name || '',
            introducing_party: arbitration.introducing_party
              ? arbitration.introducing_party.split(',').map((p) => p.trim())
              : [],
            date: arbitration.date || '',
            details: arbitration.details || '',
            location: arbitration.location || '',
            session_passcode: arbitration.session_passcode || generatePasscode(),
            schedule_video: !!arbitration.schedule_video,
            zoom_tech: arbitration.zoom_tech || (arbitration.schedule_video ? 'zoom' : ''),
            registrants: arbitration.registrants || '',
            meeting_duration: arbitration.meeting_duration || '60',
            meeting_time: arbitration.meeting_time || '',
            meeting_timezone: arbitration.meeting_timezone || '',
            zoom_meeting_id: arbitration.zoom_meeting_id || '',
            zoom_join_url: arbitration.zoom_join_url || '',
            zoom_start_url: arbitration.zoom_start_url || '',
            zoom_password: arbitration.zoom_password || arbitration.session_passcode || '',
            zoom_created_at: arbitration.zoom_created_at || '',
          });
        } catch (err) {
          console.error('loadArbitration error:', err);
          console.error('response error:', err.response?.data);
          toast.error(
            displayText('Failed to load arbitration data: ') +
              (err.response?.data?.message || err.message || 'Unknown error')
          );
          navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${paramCaseId}`);
        }
      };

      loadArbitration();
    }
  }, [paramCaseId, arbitrationId, isEditMode, navigate, username]);

  /* ================= FETCH CASE DETAILS ================= */
  useEffect(() => {
    if (!caseId || isNaN(caseId)) return;

    const fetchCase = async () => {
      try {
        const res = await getCase(caseId);
        setCaseData(res.data.data);
      } catch (err) {
        toast.error(displayText('Failed to fetch case details'));
        navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`);
      }
    };

    fetchCase();
  }, [caseId, navigate, username]);

  /* ================= HANDLERS ================= */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};

    if (name === 'schedule_video') {
      const isEnabled = checked;

      setFormData((prev) => ({
        ...prev,
        schedule_video: isEnabled,
        zoom_tech: isEnabled ? 'zoom' : '',
        registrants: isEnabled ? prev.registrants : '',
        meeting_duration: isEnabled ? (prev.meeting_duration || '60') : '',
        meeting_time: isEnabled ? prev.meeting_time : '',
        meeting_timezone: isEnabled ? prev.meeting_timezone : '',
        zoom_meeting_id: isEnabled ? prev.zoom_meeting_id : '',
        zoom_join_url: isEnabled ? prev.zoom_join_url : '',
        zoom_start_url: isEnabled ? prev.zoom_start_url : '',
        zoom_password: isEnabled ? (prev.session_passcode || '') : '',
        zoom_created_at: isEnabled ? prev.zoom_created_at : '',
      }));

      setErrors((prev) => ({
        ...prev,
        zoom_tech: '',
        registrants: '',
        meeting_duration: '',
        meeting_time: '',
        meeting_timezone: '',
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'arbitration');
  };

  const handleIntroducingPartySelect = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue && !formData.introducing_party.includes(selectedValue)) {
      setFormData((prev) => ({
        ...prev,
        introducing_party: [...prev.introducing_party, selectedValue],
      }));
      e.target.value = '';
      setTouched(prev => ({ ...prev, introducing_party: false }));
    }
  };

  const removeIntroducingParty = (partyToRemove) => {
    setFormData((prev) => {
      const updated = prev.introducing_party.filter((p) => p !== partyToRemove);
      // If list becomes empty after removal, mark as touched to show error
      if (updated.length === 0) {
        setTouched(t => ({ ...t, introducing_party: true }));
      }
      return { ...prev, introducing_party: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(prev => ({ ...prev, introducing_party: true }));
    if (isSubmitting) return;

    setIsSubmitting(true);
    setIsLoading(true);

    if (formData.introducing_party.length === 0) {
      toast.error('Please select at least one introducing party');
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataForValidation = {
        ...formData,
        introducing_party: formData.introducing_party
          .map((name) => name.replace(/^"|"$/g, '').trim())
          .join(', '),
      };

      const isValid = await validateForm(formDataForValidation, setErrors, 'arbitration');

      if (!isValid) {
        toast.error('Please fill all required fields correctly.');
        throw new Error('Validation failed');
      }

      const payload = {
        case_id: caseId,
        name: formData.name,
        introducing_party: formDataForValidation.introducing_party,
        date: formData.date,
        details: formData.details,
        location: formData.location,
        session_passcode: formData.session_passcode,
        schedule_video: formData.schedule_video,
        zoom_tech: formData.schedule_video ? formData.zoom_tech || 'zoom' : null,
        registrants: formData.schedule_video ? formData.registrants : null,
        meeting_duration: formData.schedule_video ? formData.meeting_duration : null,
        meeting_time: formData.schedule_video ? formData.meeting_time : null,
        meeting_timezone: formData.schedule_video ? formData.meeting_timezone : null,
      };

      console.log('Sending arbitration payload:', payload);

      let response;

      if (isEditMode) {
        response = await updateArbitration(arbitrationId, payload);

        if (response.status !== 200 && response.status !== 204) {
          throw new Error(`Update failed with status ${response.status}`);
        }

        toast.success(`${SC.label} updated successfully`);
      } else {
        response = await createArbitration(payload);

        if (response.status !== 201 && response.status !== 200 && response.status !== 204) {
          throw new Error(`Create failed with status ${response.status}`);
        }

        toast.success(`${SC.label} added successfully`);
      }

      navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${paramCaseId}`);
    } catch (err) {
      console.error('Arbitration submit error:', err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        `Failed to save ${SC.label}. Please check the form and try again.`;
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${paramCaseId}`);
  };

  return (
    <>
      {/* Breadcrumb */}
      <div style={{ padding: '0rem 1.0rem 1.0rem 0rem', margin: 0 }}>
        <nav aria-label="breadcrumb">
          <ol
            className="breadcrumb mb-0"
            style={{ fontSize: '10px', padding: 0, background: 'transparent' }}
          >
            <li className="breadcrumb-item">
              <Link
                to={`/${username}/dashboard/cases`}
                style={{
                  color: theme.colors.primary,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => (e.target.style.color = theme.colors.primary + 'cc')}
                onMouseOut={(e) => (e.target.style.color = theme.colors.primary)}
              >
                {ClientConfig.cases.managementLabel}
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to={`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`}
                style={{
                  color: theme.colors.textPrimary,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => (e.target.style.color = theme.colors.primary)}
                onMouseOut={(e) => (e.target.style.color = theme.colors.textPrimary)}
              >
                {caseData?.case_name || 'Loading...'}
              </Link>
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}
            >
              {isEditMode ? 'Edit' : 'Add'}: {SC.label}
            </li>
          </ol>
        </nav>
      </div>

      {/* MAIN CONTAINER */}
      <div
        className="container-fluid py-4"
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          fontFamily:
            theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif',
          fontSize: 'clamp(10px, 2.5vw, 11px)',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
          margin: '0 1.0rem 1.0rem 0',
        }}
      >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            fontSize: '10px',
            fontWeight: '500',
          }}
        />

        <div
          style={{
            borderRadius: '10px',
            border: `1px solid ${theme.colors.borderColor}`,
            overflow: 'hidden',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              gap: '12px',
            }}
          >
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {isEditMode ? 'Edit' : 'Add'}: {SC.label}
            </h5>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              {SC.relatedToLabel} : {caseData?.case_name || 'Loading...'}
            </h5>
          </div>

          <div style={{ padding: '16px' }}>
            <form id="addArbitrationForm" onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: window.innerWidth < 600 ? 'column' : 'row',
                  gap: '24px',
                  alignItems: 'flex-start',
                }}
              >
                {/* LEFT COLUMN */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label
                      htmlFor="name"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {SC.fieldLabel}*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter SIP"
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.name ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.name && (
                      <div
                        style={{
                          color: theme.colors.danger,
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="introducingParty"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >{clientUserText(" Introducing Party* (Client Admins & Users) ")}</label>
                    <select
                      id="introducingParty"
                      onChange={handleIntroducingPartySelect}
                      onBlur={() => setTouched(prev => ({ ...prev, introducing_party: true }))}
                      disabled={isSubmitting || loadingUsers}
                      className={`form-control ${errors.introducing_party ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.introducing_party
                            ? theme.colors.danger
                            : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      <option value="">{loadingUsers ? 'Loading...' : 'Select to add...'}</option>
                      {combinedUsers.map((user) => (
                        <option key={`${user.type}-${user.id}`} value={user.display_name}>
                          {user.display_name}
                        </option>
                      ))}
                    </select>

                    {formData.introducing_party.length > 0 && (
                      <div
                        style={{
                          marginTop: '8px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        {formData.introducing_party.map((party, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: theme.colors.primary,
                              color: theme.colors.textWhite,
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '9px',
                              fontWeight: '500',
                            }}
                          >
                            <span style={{ marginRight: '6px' }}>{party}</span>
                            <button
                              type="button"
                              onClick={() => removeIntroducingParty(party)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: theme.colors.textWhite,
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: '0',
                                lineHeight: '1',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                {touched.introducing_party && formData.introducing_party.length === 0 && (
                  <div
                style={{
                fontSize: '9px',
                color: theme.colors.danger,
                marginTop: '4px',
                }}
                 >
                    Please select at least one introducing party
                 </div>
                 )}

                    {errors.introducing_party && (
                      <div
                        style={{
                          color: theme.colors.danger,
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.introducing_party}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="date"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.date ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.date && (
                      <div
                        style={{
                          color: theme.colors.danger,
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.date}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="details"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      Details
                    </label>
                    <textarea
                      id="details"
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.details ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        resize: 'vertical',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.details && (
                      <div
                        style={{
                          color: theme.colors.danger,
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.details}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="location"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >
                      {SC.locationLabel}
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${
                          errors.location ? theme.colors.danger : theme.colors.borderColor
                        }`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.location && (
                      <div
                        style={{
                          color: theme.colors.danger,
                          fontSize: '9px',
                          fontWeight: '500',
                          marginTop: '2px',
                        }}
                      >
                        {errors.location}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="sessionPasscode"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        display: 'block',
                      }}
                    >{displayText(" Session Passcode* (Auto-Generated) ")}</label>
                    <input
                      type="text"
                      id="sessionPasscode"
                      name="session_passcode"
                      value={formData.session_passcode}
                      readOnly
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        fontWeight: '100',
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        transition: 'border-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                        fontFamily: 'inherit',
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>
                </div>

                {/* DIVIDER */}
                <div
                  style={{
                    width: window.innerWidth < 600 ? '100%' : '1px',
                    height: window.innerWidth < 600 ? '1px' : 'auto',
                    backgroundColor: 'transparent',
                    backgroundImage:
                      window.innerWidth < 600
                        ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`
                        : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`,
                    margin: window.innerWidth < 600 ? '12px 0' : '0 16px',
                    minHeight: window.innerWidth >= 600 ? '400px' : '1px',
                  }}
                />

                {/* RIGHT COLUMN */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label
                      htmlFor="scheduleVideo"
                      style={{
                        color: theme.colors.textPrimary,
                        fontSize: '10px',
                        fontWeight: '600',
                        margin: 0,
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      Schedule a Video Conference?
                    </label>

                    <div
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: formData.schedule_video
                          ? theme.colors.success
                          : '#ccc',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.3s ease',
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                      onClick={() => {
                        if (!isSubmitting) {
                          const nextValue = !formData.schedule_video;

                          setFormData((prev) => ({
                            ...prev,
                            schedule_video: nextValue,
                            zoom_tech: nextValue ? 'zoom' : '',
                            registrants: nextValue ? prev.registrants : '',
                            meeting_duration: nextValue
                              ? prev.meeting_duration || '60'
                              : '',
                            meeting_time: nextValue ? prev.meeting_time : '',
                            meeting_timezone: nextValue ? prev.meeting_timezone : '',
                            zoom_meeting_id: nextValue ? prev.zoom_meeting_id : '',
                            zoom_join_url: nextValue ? prev.zoom_join_url : '',
                            zoom_start_url: nextValue ? prev.zoom_start_url : '',
                            zoom_password: nextValue
                              ? prev.session_passcode || ''
                              : '',
                            zoom_created_at: nextValue ? prev.zoom_created_at : '',
                          }));

                          setErrors((prev) => ({
                            ...prev,
                            zoom_tech: '',
                            registrants: '',
                            meeting_duration: '',
                            meeting_time: '',
                            meeting_timezone: '',
                          }));
                        }
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: formData.schedule_video ? '28px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: theme.colors.textWhite,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'left 0.3s ease',
                        }}
                      />
                      <input
                        type="checkbox"
                        id="scheduleVideo"
                        name="schedule_video"
                        checked={formData.schedule_video}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>

                  {formData.schedule_video && (
                    <>
                      <div>
                        <label
                          htmlFor="zoomMeetingId"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Meeting ID
                        </label>
                        <input
                          type="text"
                          id="zoomMeetingId"
                          name="zoom_meeting_id"
                          value={formData.zoom_meeting_id}
                          readOnly
                          disabled
                          className="form-control"
                          placeholder="Will be generated after Zoom meeting is created"
                          style={{
                            width: '100%',
                            border: `1px solid ${theme.colors.borderColor}`,
                            borderRadius: '4px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: '100',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            outline: 'none',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                            fontFamily: 'inherit',
                            cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="zoomJoinUrl"
                          style={{
                            color: theme.colors.textPrimary,
                            fontSize: '10px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            display: 'block',
                          }}
                        >
                          Zoom Join URL
                        </label>
  <input
    type="text"
    id="zoomJoinUrl"
    name="zoom_join_url"
    value={formData.zoom_join_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomStartUrl"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Start URL
  </label>
  <input
    type="text"
    id="zoomStartUrl"
    name="zoom_start_url"
    value={formData.zoom_start_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomPassword"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Password
  </label>
  <input
    type="text"
    id="zoomPassword"
    name="zoom_password"
    value={formData.zoom_password}
    readOnly
    disabled
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label
    htmlFor="zoomCreatedAt"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Zoom Created At
  </label>
  <input
    type="text"
    id="zoomCreatedAt"
    name="zoom_created_at"
    value={formData.zoom_created_at}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be filled after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: '#f0f0f0',
      color: '#333',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
      cursor: 'not-allowed',
    }}
  />
</div>
<div>
  <label
    htmlFor="meetingDuration"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Duration (mins)
  </label>
  <input
    type="number"
    id="meetingDuration"
    name="meeting_duration"
    value={formData.meeting_duration}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    min="15"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  />
</div>

<div>
  <label
    htmlFor="meetingTime"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Time
  </label>
  <input
    type="time"
    id="meetingTime"
    name="meeting_time"
    value={formData.meeting_time}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  />
</div>

<div>
  <label
    htmlFor="meetingTimezone"
    style={{
      color: theme.colors.textPrimary,
      fontSize: '10px',
      fontWeight: '600',
      marginBottom: '4px',
      display: 'block'
    }}
  >
    Meeting Timezone
  </label>
  <select
    id="meetingTimezone"
    name="meeting_timezone"
    value={formData.meeting_timezone}
    onChange={handleInputChange}
    disabled={isSubmitting}
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      fontWeight: '100',
      backgroundColor: theme.colors.bgPrimary,
      color: '#666',
      outline: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: 'inherit',
    }}
  >
    <option value="">Select timezone</option>
    <option value="Asia/Kolkata">Asia/Kolkata</option>
    <option value="UTC">UTC</option>
    <option value="America/New_York">America/New_York</option>
    <option value="America/Los_Angeles">America/Los_Angeles</option>
  </select>
</div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            padding: '12px',
            backgroundColor: theme.colors.bgPrimary,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexDirection: window.innerWidth < 600 ? 'column' : 'row',
              width: window.innerWidth < 600 ? '100%' : 'auto',
            }}
          >
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                background: theme.gradients.secondary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                transition: 'opacity 0.3s ease',
                width: window.innerWidth < 600 ? '100%' : 'auto',
                minWidth: '120px',
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="addArbitrationForm"
              disabled={isLoading}
              style={{
                background: theme.gradients.primary,
                color: theme.colors.textWhite,
                fontSize: '9px',
                fontWeight: '600',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                transition: 'opacity 0.3s ease',
                width: window.innerWidth < 600 ? '100%' : 'auto',
                minWidth: '120px',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </button>
          </div>

          {isLoading && (
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: `4px solid ${theme.colors.primary}`,
                  borderTop: '4px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          )}
        </div>

        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );
};

export default AddArbitration;